"use client";

import React from "react";
import { DateCalendar, LocalizationProvider } from "@mui/x-date-pickers";
import { DayCalendarSkeleton } from "@mui/x-date-pickers/DayCalendarSkeleton";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { Box } from "@mui/material";
import ExpandLessOutlinedIcon from "@mui/icons-material/ExpandLessOutlined";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import CustomDay from "./CustomDay";

const CalendarSwitchViewIcon = (props) => {
  // MUI passes ownerState with the active view.
  const isMonthView = props?.ownerState?.view === "month";
  return isMonthView ? (
    <ExpandLessOutlinedIcon {...props} />
  ) : (
    <ExpandMoreOutlinedIcon {...props} />
  );
};

const CalendarPicker = ({
  theme,
  selectedMoment,
  onDateChange,
  minDate,
  maxDate,
  calendarView,
  onViewChange,
  onMonthChange,
  selectedDay,
  availability,
  cutoffDate,
  meetingDuration,
  userTimezone,
  checkDayAvailability,
  checkDaySelectable,
  hasMeetingDay,
  isLoading = false,
  headerLabelFontSize = "1.1rem",
  weekDayLabelFontSize = undefined,
  weekDayLabelFontWeight = undefined,
  dayOfWeekFormatter,
  showAvailabilityDot = true,
  showMeetingDot = false,
  availabilityDotColor,
  meetingDotColor,
  todayBackgroundColor,
  todayTextColor,
  dayNumberFontSize,
  dayNumberFontWeight,
  softSelection = false,
  sx = {},
}) => {
  const calendarTheme = createTheme({
    ...theme,
    palette: {
      ...theme.palette,
      primary: {
        ...theme.palette.primary,
        dark: theme.palette.grey[400],
      },
    },
    components: {
      MuiDateCalendar: {
        styleOverrides: {
          root: {
            // Remove custom padding/margin that affects sizing
            // padding: "0px",
            // margin: "0px"
          },
        },
      },
      MuiMonthCalendar: {
        styleOverrides: {
          root: {
            width: "100%",
          },
        },
      },
      MuiPickersMonth: {
        styleOverrides: {
          monthButton: {
            fontSize: "0.72rem",
            fontWeight: 500,
          },
        },
      },
      // Remove the skeleton overrides - let it use defaults
      // MuiDayCalendarSkeleton: {
      //   styleOverrides: {
      //     root: {
      //       marginTop: "-8px",
      //       marginLeft: "-4px",
      //     },
      //     week: {
      //       gap: "6px",
      //     },
      //     daySkeleton: {
      //       margin: "2px",
      //     },
      //   },
      // },
      MuiPickersToolbar: {
        styleOverrides: {
          root: { display: "none" },
        },
      },
      MuiPickerDay: {
        styleOverrides: {
          root: {
            // Keep your custom colors but remove size overrides
            // fontSize: ".95rem", // Remove this - let it use default size
            "&.MuiPickerDay-today": {
              backgroundColor: `${theme.palette.grey[400]} !important`,
              border: "none !important",
            },
            "&.Mui-selected": {
              backgroundColor: `${theme.palette.primary.main} !important`,
              color: `white !important`,
              border: "none !important",
            },
            "&.Mui-selected:hover": {
              backgroundColor: `${theme.palette.primary.main} !important`,
            },
            "&:hover": {
              backgroundColor: `${theme.palette.grey[300]} !important`,
            },
          },
        },
      },
      MuiDayCalendar: {
        styleOverrides: {
          weekDayLabel: {
            fontWeight: weekDayLabelFontWeight || "bold",
            color: theme.palette.text.primary,
            ...(weekDayLabelFontSize ? { fontSize: weekDayLabelFontSize } : {}),
          },
        },
      },
      MuiPickersCalendarHeader: {
        styleOverrides: {
          root: {
            marginTop: "0px",
            paddingLeft: "0px",
            paddingRight: "0px",
            marginBottom: "20px",
            minHeight: "30px",
            maxHeight: "30px",
          },
          label: {
            fontSize: headerLabelFontSize,
            fontWeight: 500,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
          },
          switchViewButton: {
            marginLeft: "2px",
            padding: 0,
            width: 28,
            height: 28,
          },
          switchViewIcon: {
            fontSize: "2rem",
          },
        },
      },
    },
  });

  return (
    <ThemeProvider theme={calendarTheme}>
      <LocalizationProvider dateAdapter={AdapterMoment} locale="en">
        <DateCalendar
          value={selectedMoment}
          onChange={onDateChange}
          minDate={minDate}
          maxDate={maxDate}
          view={calendarView}
          onViewChange={onViewChange}
          onMonthChange={onMonthChange}
          slots={{
            day: CustomDay,
            toolbar: () => null,
            switchViewIcon: CalendarSwitchViewIcon,
          }}
          slotProps={{
            day: {
              selectedDay,
              availability,
              cutoffDate,
              meetingDuration,
              theme,
              userTimezone,
              checkDayAvailability,
              checkDaySelectable,
              hasMeetingDay,
              showAvailabilityDot,
              showMeetingDot,
              availabilityDotColor,
              meetingDotColor,
              todayBackgroundColor,
              todayTextColor,
              dayNumberFontSize,
              dayNumberFontWeight,
              softSelection,
            },
          }}
          disableHighlightToday
          views={["day", "month"]}
          dayOfWeekFormatter={dayOfWeekFormatter}
          showDaysOutsideCurrentMonth={false}
          orientation="portrait"
          autoFocus={false}
          loading={isLoading}
          renderLoading={() => (
            <Box sx={{ width: "100%", maxWidth: "none" }}>
              <DayCalendarSkeleton
                sx={{
                  mt: -1,
                  ml: -0.5,
                  width: "100%",
                  "& .MuiDayCalendarSkeleton-week": {
                    gap: "6px",
                  },
                  "& .MuiDayCalendarSkeleton-daySkeleton": {
                    m: 0.25,
                  },
                }}
              />
            </Box>
          )}
          sx={{
            "& .MuiPickersMonth-monthButton": {
              fontSize: "0.72rem",
              fontWeight: 500,
            },
            "& .MuiDayCalendarSkeleton-root": {
              width: "100%",
              maxWidth: "none",
            },
            ...sx,
          }}
        />
      </LocalizationProvider>
    </ThemeProvider>
  );
};

export default CalendarPicker;
